/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 */

import { UserBusyTimes } from "@microsoft/shifts-models-data";
import { ShiftInfo } from "@microsoft/shifts-models-view";
// TODO: Replace 'sh-services' dependency from Web core with one from a workspace package.
import { DataService } from "sh-services/dataservice/DataService";

import { CrossLocationShiftBaseParams } from "./CrossLocationShiftBaseParams";
import { CrossLocationShiftDataProvider } from "./CrossLocationShiftDataProvider";
import { CrossLocationShiftMapper } from "./CrossLocationShiftMapper";
import { ICrossLocationShiftDataService } from "./ICrossLocationShiftDataService";

/**
 * The interface for cross-location shift data service.
 */
export class CrossLocationShiftDataService extends DataService implements ICrossLocationShiftDataService {
    /**
     * Gets the cross-location shifts for the given team id and date-times interval.
     * @param params The parameters.
     * @param setDataInState The callback to set the view models data in state.
     * @returns The cross-location shifts.
     */
    public async getCrossLocationShifts(params: CrossLocationShiftBaseParams, setDataInState: (shifts: ShiftInfo[]) => void): Promise<ShiftInfo[]> {
        const { endTimestampMs, teamId, startTimestampMs } = params;
        const mapper = new CrossLocationShiftMapper();

        const provider = new CrossLocationShiftDataProvider({
            database: this.crossLocationShiftDatabase,
            endTimestampMs,
            setDataInMemory: (usersShifts: UserBusyTimes[]): void => {
                this.setDataInMemory(usersShifts, setDataInState);
            },
            startTimestampMs,
            teamId,
            tenantId: this.tenantId
        });

        const usersShifts = await this.getData<UserBusyTimes[]>(provider);

        return mapper.toShiftsInfo(usersShifts);
    }

    /**
     * Resets the sync state.
     */
    public async resetSyncState(): Promise<void> {
        return Promise.resolve();
    }

    /**
     * Converts data models to view models and set them in the state.
     * @param usersShifts The users shifts data models.
     * @param setDataInState The callback to set view models in the state.
     */
    private setDataInMemory(usersShifts: UserBusyTimes[], setDataInState: (shifts: ShiftInfo[]) => void): void {
        const mapper = new CrossLocationShiftMapper();
        const shifts = mapper.toShiftsInfo(usersShifts);

        setDataInState(shifts);
    }
}
